import React, { useMemo, useRef, useState, useEffect } from 'react';
import Badge from '../ui/Badge';
import { STATUS_FAMILIES } from '../../utils/gpsStatusCategories';
import { METHODOLOGY_INTRO, METHODOLOGY_SECTIONS } from './gpsMethodology.config';

const KIND_TONE = {
  compte: 'blue',
  'dérivé': 'violet',
  moyenne: 'green',
  distinct: 'slate',
  'série': 'blue',
  'répartition': 'green',
};

const SENSE_DOT = {
  Tout: '#5b3df0',
  Reçus: '#2f6df0',
  Fermés: '#16a866',
  Encours: '#3a4a63',
};

const Card = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-[#1e1e1e] border border-gray-200 dark:border-gray-900 rounded-2xl p-5 ${className}`}>
    {children}
  </div>
);

// Compact field cross-reference: a single dropdown (searchable + counts) rather
// than a long scrolling chip bar.
const FieldFilter = ({ field, setField, fields, counts }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const filtered = fields.filter((f) => f.toLowerCase().includes(q.trim().toLowerCase()));
  const pick = (f) => { setField(f); setOpen(false); setQ(''); };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs font-semibold transition-colors ${
          field
            ? 'border-[#5b3df0] text-[#5b3df0] dark:text-[#a98ff5] bg-[#f3f1fe] dark:bg-[#2a2440]'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1e1e1e] text-gray-600 dark:text-gray-300 hover:border-[#5b3df0]'
        }`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 4h18l-7 8v6l-4 2v-8z" />
        </svg>
        <span>{field ? <>Champ&nbsp;: <code>{field}</code></> : 'Filtrer par champ'}</span>
        <svg className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>
      </button>

      {open && (
        <div className="absolute right-0 z-20 mt-1.5 w-64 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1e1e1e] shadow-xl p-1.5">
          <input
            autoFocus
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un champ…"
            className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#161616] px-2.5 py-1.5 text-xs text-gray-900 dark:text-white outline-none focus:border-[#5b3df0] mb-1.5"
          />
          <div className="max-h-72 overflow-y-auto">
            <button
              type="button"
              onClick={() => pick(null)}
              className={`flex items-center w-full rounded-lg px-2 py-1.5 text-xs text-left ${!field ? 'bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5] font-semibold' : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'}`}
            >
              Tous les champs
            </button>
            {filtered.map((f) => {
              const active = field === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => pick(f)}
                  className={`flex items-center gap-2 w-full rounded-lg px-2 py-1.5 text-xs text-left ${active ? 'bg-[#f3f1fe] dark:bg-[#2a2440]' : 'hover:bg-gray-50 dark:hover:bg-gray-800'}`}
                >
                  <code className={active ? 'text-[#5b3df0] dark:text-[#a98ff5] font-semibold' : 'text-gray-700 dark:text-gray-300'}>{f}</code>
                  <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500">{counts.get(f)}</span>
                </button>
              );
            })}
            {filtered.length === 0 && (
              <div className="px-2 py-3 text-xs text-gray-400 dark:text-gray-500 text-center">Aucun champ</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const GPSMethodology = () => {
  const { sense, statuses } = METHODOLOGY_INTRO;
  const statusColor = (key) => (STATUS_FAMILIES.find((f) => f.key === key) || {}).color || '#9ca3af';

  const [field, setField] = useState(null);
  const [activeSection, setActiveSection] = useState(METHODOLOGY_SECTIONS[0]?.id || null);
  const sectionRefs = useRef({});

  // Distinct issue fields used across all indicators, with how many KPIs use each.
  const fieldCounts = useMemo(() => {
    const m = new Map();
    METHODOLOGY_SECTIONS.forEach((s) => s.items.forEach((it) => (it.props || []).forEach((p) => m.set(p, (m.get(p) || 0) + 1))));
    return m;
  }, []);
  const allFields = useMemo(() => Array.from(fieldCounts.keys()).sort(), [fieldCounts]);

  // Sections filtered by the selected field (cross-reference); empty ones drop out.
  const visibleSections = useMemo(() => {
    if (!field) return METHODOLOGY_SECTIONS;
    return METHODOLOGY_SECTIONS
      .map((s) => ({ ...s, items: s.items.filter((it) => (it.props || []).includes(field)) }))
      .filter((s) => s.items.length > 0);
  }, [field]);
  const visibleIds = useMemo(() => new Set(visibleSections.map((s) => s.id)), [visibleSections]);

  // Scroll-spy: highlight the section currently near the top of the viewport.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const els = visibleSections.map((s) => sectionRefs.current[s.id]).filter(Boolean);
    if (!els.length) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        const inView = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (inView[0]) setActiveSection(inView[0].target.dataset.sid);
      },
      { rootMargin: '-15% 0px -75% 0px', threshold: 0 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [visibleSections]);

  const goTo = (id) => {
    setActiveSection(id);
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const pillBase = 'px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap';

  return (
    <div className="space-y-6 text-left">
      {/* Header */}
      <div className="flex items-center gap-3">
        <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5] flex-none">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" /><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
          </svg>
        </span>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Lexique &amp; Définitions</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 leading-tight">Comment sont calculés les indicateurs et graphes du module GPS.</p>
        </div>
      </div>

      {/* Sticky toolbar: section nav (wraps) + compact field filter (right) */}
      <div className="sticky top-0 z-10 -mx-4 px-4 py-3 bg-gray-50/95 dark:bg-[#0d0d0d]/95 backdrop-blur border-b border-gray-200 dark:border-gray-900">
        <div className="flex items-center gap-1.5 flex-wrap">
          {METHODOLOGY_SECTIONS.map((s) => {
            const dimmed = !visibleIds.has(s.id);
            const active = activeSection === s.id && !dimmed;
            return (
              <button
                key={s.id}
                type="button"
                onClick={() => !dimmed && goTo(s.id)}
                disabled={dimmed}
                className={`${pillBase} ${
                  active
                    ? 'bg-[#5b3df0] text-white'
                    : dimmed
                      ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                      : 'bg-white dark:bg-[#1e1e1e] border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-[#5b3df0] hover:text-[#5b3df0]'
                }`}
              >
                {s.title}
              </button>
            );
          })}
          <div className="ml-auto">
            <FieldFilter field={field} setField={setField} fields={allFields} counts={fieldCounts} />
          </div>
        </div>
      </div>

      {/* Intro: sense + status taxonomy (hidden while cross-referencing a field) */}
      {!field && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card>
            <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">{sense.title}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{sense.text}</p>
            <div className="space-y-2">
              {sense.items.map((it) => (
                <div key={it.label} className="flex items-start gap-2.5 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm flex-none mt-0.5" style={{ background: SENSE_DOT[it.label] || '#9ca3af' }} />
                  <span><span className="font-semibold text-gray-900 dark:text-gray-100">{it.label}</span> <span className="text-gray-500 dark:text-gray-400">— {it.desc}</span></span>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div className="text-sm font-bold text-gray-900 dark:text-white mb-1">{statuses.title}</div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{statuses.text}</p>
            <div className="space-y-2">
              {statuses.items.map((it) => (
                <div key={it.key} className="flex items-start gap-2.5 text-xs">
                  <span className="w-2.5 h-2.5 rounded-sm flex-none mt-0.5" style={{ background: statusColor(it.key) }} />
                  <span><span className="font-semibold text-gray-900 dark:text-gray-100">{it.label}</span> <span className="text-gray-500 dark:text-gray-400">— {it.desc}</span></span>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-start gap-2 rounded-lg border border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 px-3 py-2 text-[11px] text-gray-500 dark:text-gray-400">
              <span className="w-2.5 h-2.5 rounded-sm bg-gray-400 flex-none mt-0.5" />
              <span>{statuses.draftNote}</span>
            </div>
          </Card>
        </div>
      )}

      {/* Active field hint */}
      {field && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Indicateurs basés sur le champ <code className="text-[#5b3df0] dark:text-[#a98ff5] font-semibold">{field}</code> ({fieldCounts.get(field)}).
        </p>
      )}

      {/* Sections */}
      {visibleSections.map((section) => (
        <section
          key={section.id}
          data-sid={section.id}
          ref={(el) => { sectionRefs.current[section.id] = el; }}
          className="scroll-mt-28"
        >
          <div className="mb-3">
            <h3 className="text-base font-bold text-gray-900 dark:text-white">{section.title}</h3>
            {section.subtitle && <p className="text-xs text-gray-500 dark:text-gray-400">{section.subtitle}</p>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {section.items.map((item) => (
              <Card key={item.name} className="flex flex-col gap-2.5">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-semibold text-gray-900 dark:text-white">{item.name}</span>
                  {item.kind && <Badge tone={KIND_TONE[item.kind] || 'gray'}>{item.kind}</Badge>}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-300">{item.measures}</p>
                <p className="text-[12.5px] text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/40 rounded-lg px-3 py-2 leading-snug">
                  <span className="font-semibold text-gray-600 dark:text-gray-300">Calcul&nbsp;: </span>{item.rule}
                </p>
                {item.props && item.props.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {item.props.map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setField(p)}
                        title={`Filtrer par ${p}`}
                        className={`text-[11px] font-medium px-2 py-0.5 rounded-md transition-colors ${
                          field === p
                            ? 'bg-[#5b3df0] text-white'
                            : 'bg-violet-100 dark:bg-violet-900/40 text-violet-700 dark:text-violet-200 hover:bg-violet-200 dark:hover:bg-violet-900/70'
                        }`}
                      >
                        <code>{p}</code>
                      </button>
                    ))}
                  </div>
                )}
                {item.note && (
                  <div className="flex items-start gap-2 text-[11px] text-gray-500 dark:text-gray-400">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5b3df0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-none mt-0.5">
                      <circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" />
                    </svg>
                    <span><span className="font-semibold text-gray-600 dark:text-gray-300">Bon à savoir&nbsp;:</span> {item.note}</span>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
};

export default GPSMethodology;
